import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const BLOCK_NAMES = {
  tech: '🔧 Тех. работы', design: '🎨 Дизайн', video: '🎬 Видео',
  loyalty: '🎁 Лояльность', social: '📱 Контент',
};

function toLocalDate(d) {
  return d.toLocaleDateString('sv-SE'); // YYYY-MM-DD
}

const PRESETS = [
  { key: 'all', label: 'Все время' },
  { key: '7d', label: '7 дней' },
  { key: '30d', label: '30 дней' },
  { key: 'lastMonth', label: 'Прошлый месяц' },
  { key: 'currentMonth', label: 'Текущий месяц' },
  { key: 'custom', label: 'Выбрать даты' },
];

function getPresetDates(key) {
  const now = new Date();
  if (key === 'all') return { from: '', to: '' };
  if (key === '7d') {
    const from = new Date(now); from.setDate(now.getDate() - 6);
    return { from: toLocalDate(from), to: toLocalDate(now) };
  }
  if (key === '30d') {
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: toLocalDate(from), to: toLocalDate(now) };
  }
  if (key === 'lastMonth') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toLocalDate(first), to: toLocalDate(last) };
  }
  if (key === 'currentMonth') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toLocalDate(first), to: toLocalDate(now) };
  }
  return { from: '', to: '' };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preset, setPreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/api/admin/stats', { params });
      setStats(res.data);
    } catch {
      setError('Ошибка загрузки статистики');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handlePreset = (key) => {
    setPreset(key);
    if (key !== 'custom') {
      const { from, to } = getPresetDates(key);
      setDateFrom(from);
      setDateTo(to);
    }
  };

  const goTickets = (status) => navigate(`/admin/tickets${status ? `?status=${status}` : ''}`);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Статистика</h1>
        <div className="date-filters">
          <div className="date-presets">
            {PRESETS.map(p => (
              <button
                key={p.key}
                className={`preset-btn ${preset === p.key ? 'active' : ''}`}
                onClick={() => handlePreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </>
          )}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="page-loader">Загрузка...</div>
      ) : stats && (
        <>
          <div className="stats-grid">
            <StatCard label="Всего заявок" value={stats.total} color="blue"
              hint="нажмите для просмотра" onClick={() => goTickets('')} />
            <StatCard label="Новые" value={stats.by_status?.new || 0} color="purple"
              hint="→ в фильтр" onClick={() => goTickets('new')} />
            <StatCard label="В работе" value={stats.by_status?.in_progress || 0} color="yellow"
              hint="→ в фильтр" onClick={() => goTickets('in_progress')} />
            <StatCard label="Закрыты" value={stats.by_status?.closed || 0} color="green"
              hint="→ в фильтр" onClick={() => goTickets('closed')} />
            <StatCard label="Срочные" value={stats.by_priority?.urgent || 0} color="red"
              hint="→ в фильтр" onClick={() => navigate('/admin/tickets?priority=urgent')} />
            <StatCard label="Ожидают регистрации" value={stats.pending_users || 0} color="orange"
              onClick={() => navigate('/admin/users')} />
          </div>

          <div className="stats-row">
            <div className="stats-card">
              <h3>По категориям</h3>
              {Object.entries(stats.by_block || {}).map(([key, val]) => (
                <div key={key} className="stat-bar-row">
                  <span>{BLOCK_NAMES[key] || key}</span>
                  <div className="stat-bar">
                    <div className="stat-bar-fill" style={{ width: `${stats.total ? (val / stats.total) * 100 : 0}%` }} />
                  </div>
                  <span className="stat-bar-val">{val}</span>
                </div>
              ))}
            </div>

            <div className="stats-card">
              <h3>По приоритету</h3>
              {[
                { key: 'urgent', label: '🔴 Срочно' },
                { key: 'normal', label: '🟡 Обычно' },
                { key: 'low', label: '🟢 Не горит' },
              ].map(({ key, label }) => (
                <div key={key} className="stat-bar-row">
                  <span>{label}</span>
                  <div className="stat-bar">
                    <div className="stat-bar-fill priority" style={{ width: `${stats.total ? ((stats.by_priority?.[key] || 0) / stats.total) * 100 : 0}%` }} />
                  </div>
                  <span className="stat-bar-val">{stats.by_priority?.[key] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color, hint, onClick }) {
  return (
    <div className={`stat-card stat-card--${color}${onClick ? ' clickable' : ''}`} onClick={onClick}>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
      {hint && <div className="stat-card-hint">{hint}</div>}
    </div>
  );
}
